<template>
  <div class="reqs">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ t('requests.inboxTitle') }}</h1>
        <p class="page-sub">{{ t('requests.inboxSub') }}</p>
      </div>
    </div>

    <div v-if="loading" class="spinner"></div>
    <div v-else-if="pending.length === 0 && reviewed.length === 0" class="empty-state"><p>{{ t('requests.inboxEmpty') }}</p></div>

    <template v-else>
      <div v-if="pending.length" class="req-cards">
        <div v-for="r in pending" :key="r.id" class="req-card">
          <div class="rc-head">
            <span class="req-status st-pending">{{ t('requests.pending') }}</span>
            <span class="rc-type">{{ r.type === 'project' ? t('requests.typeProject') : t('requests.typeTask') }}</span>
          </div>
          <div class="rc-title">{{ r.title }}</div>
          <div class="rc-from">{{ t('requests.from') }} <strong>{{ r.client_name }}</strong></div>
          <p v-if="r.description" class="rc-desc">{{ r.description }}</p>
          <div v-if="r.desired_pricing_type !== 'none'" class="rc-bill">
            {{ t('requests.billingWanted') }}: {{ pricingLabel(r) }}
          </div>
          <div class="rc-actions">
            <button class="btn btn-sm btn-ghost danger-text" :disabled="busy === r.id" @click="decline(r)">{{ t('requests.decline') }}</button>
            <button class="btn btn-sm btn-primary" :disabled="busy === r.id" @click="approve(r)">{{ t('requests.approve') }}</button>
          </div>
        </div>
      </div>

      <template v-if="reviewed.length">
        <div class="section-header"><h2 class="section-title">{{ t('requests.reviewed') }}</h2></div>
        <div class="req-list">
          <div v-for="r in reviewed" :key="r.id" class="req-item">
            <div class="req-main">
              <div class="req-title">{{ r.title }}</div>
              <div class="req-meta">{{ r.client_name }} · {{ r.type === 'project' ? t('requests.typeProject') : t('requests.typeTask') }}</div>
            </div>
            <span :class="['req-status', 'st-' + r.status]">{{ t('requests.' + r.status) }}</span>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import api from '@/api'
import { useI18n } from '@/composables/useI18n'
import { onRealtime, debounce } from '@/composables/useRealtime'
import { fmtMoney } from '@/utils/billing'

export default {
  name: 'RequestsView',
  setup() {
    const { t } = useI18n()
    const all = ref([])
    const loading = ref(true)
    const busy = ref(null)

    const pending  = computed(() => all.value.filter(r => r.status === 'pending'))
    const reviewed = computed(() => all.value.filter(r => r.status !== 'pending'))

    const pricingLabel = (r) => {
      const lbl = r.desired_pricing_type === 'fixed' ? t('projects.pricingFixed') : t('projects.pricingHourly')
      return r.desired_price_amount != null ? `${lbl} · ${fmtMoney(r.desired_price_amount)}` : lbl
    }

    async function load() {
      loading.value = true
      try { all.value = (await api.getRequests()).data || [] } catch {} finally { loading.value = false }
    }
    async function approve(r) {
      busy.value = r.id
      try { await api.approveRequest(r.id); await load() } catch {} finally { busy.value = null }
    }
    async function decline(r) {
      if (!confirm(t('requests.declineConfirm', { title: r.title }))) return
      busy.value = r.id
      try { await api.declineRequest(r.id); await load() } catch {} finally { busy.value = null }
    }

    let off = () => {}
    onMounted(() => { load(); off = onRealtime('request.changed', debounce(load, 400)) })
    onUnmounted(() => off())

    return { t, pending, reviewed, loading, busy, approve, decline, pricingLabel }
  },
}
</script>

<style scoped>
.page-sub { color: var(--muted); font-size: 13px; margin-top: 2px; }
.req-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 12px; margin-bottom: 8px; }
.req-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 16px; }
.rc-head { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
.rc-type { font-size: 11px; color: var(--muted); text-transform: uppercase; letter-spacing: .04em; }
.rc-title { font-size: 15px; font-weight: 600; }
.rc-from { font-size: 12px; color: var(--muted); margin-top: 3px; }
.rc-desc { font-size: 13px; line-height: 1.5; margin-top: 10px; white-space: pre-wrap; }
.rc-bill { font-size: 12px; color: var(--text); margin-top: 10px; background: var(--accent-wash); border-radius: 8px; padding: 6px 10px; display: inline-block; }
.rc-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 14px; }
.danger-text { color: var(--danger); }

.section-header { margin: 26px 0 14px; }
.section-title { font-size: 16px; font-weight: 600; }
.req-list { display: flex; flex-direction: column; gap: 8px; }
.req-item { display: flex; align-items: center; gap: 12px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 14px 16px; }
.req-main { flex: 1; min-width: 0; }
.req-title { font-size: 14px; font-weight: 500; }
.req-meta { font-size: 12px; color: var(--muted); margin-top: 2px; }
.req-status { font-size: 11px; font-weight: 600; padding: 3px 10px; border-radius: 999px; white-space: nowrap; }
.st-pending  { background: var(--accent-wash); color: var(--accent); }
.st-approved { background: rgba(30,138,83,.12); color: var(--success); }
.st-declined { background: rgba(209,68,68,.12); color: var(--danger); }
</style>
